import { ReactNode, useState } from 'react';

import { Button } from '@gearbelt/ui/components';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  GroupingState,
  RowSelectionState,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';

import ArrowLongDownIcon from '@/components/icons/arrow-long-down';
import ArrowLongUpIcon from '@/components/icons/arrow-long-up';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { ColumnFilterPopover } from './column-filter-popover';

interface TablePageTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  notFoundDisplay?: ReactNode;
  notFoundDisplayContainerClassName?: string;
  containerClassName?: string;
  cellClassName?: string;
  headClassName?: string;
  headerClassName?: string;
  tableClassName?: string;
  sortButtonClassName?: string;
  loading?: boolean;
  sorting?: SortingState;
  initialSorting?: SortingState;
  onSortingChange?: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
  grouping?: GroupingState;
  initialGrouping?: GroupingState;
  onGroupingChange?: (grouping: GroupingState) => void;
  columnVisibility?: VisibilityState;
  initialColumnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (state: VisibilityState) => void;
  rowSelection?: RowSelectionState;
  initialRowSelection?: RowSelectionState;
  onRowSelectionChange?: (state: RowSelectionState) => void;
  columnOrder?: ColumnOrderState;
  initialColumnOrder?: ColumnOrderState;
  onColumnOrderChange?: (state: ColumnOrderState) => void;
  columnFilters?: ColumnFiltersState;
  initialColumnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: (state: ColumnFiltersState) => void;
  onRowClick?: (row: T, event: React.MouseEvent<HTMLTableRowElement>) => void;
  globalFilter?: string;
}

export function TablePageTable<T>({
  data,
  columns,
  notFoundDisplay,
  notFoundDisplayContainerClassName,
  containerClassName,
  cellClassName,
  headClassName,
  headerClassName,
  loading = false,
  onSortingChange,
  initialSorting = [],
  initialGrouping = [],
  grouping: groupingOverride,
  initialColumnVisibility = {},
  columnVisibility: columnVisibilityOverride,
  initialRowSelection = {},
  onGroupingChange,
  rowSelection: rowSelectionOverride,
  sorting: sortingOverride,
  onRowClick,
  onRowSelectionChange,
  onColumnVisibilityChange,
  initialColumnOrder = [],
  columnOrder: columnOrderOverride,
  onColumnOrderChange,
  initialColumnFilters = [],
  columnFilters: columnFiltersOverride,
  onColumnFiltersChange,
  tableClassName,
  sortButtonClassName,
  globalFilter,
}: TablePageTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(initialRowSelection);
  const [grouping, setGrouping] = useState<GroupingState>(initialGrouping);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialColumnVisibility);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(initialColumnOrder);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters);

  const handleSortingChange = (updatedSorting: SortingState | ((prev: SortingState) => SortingState)) => {
    if (!sortingOverride) {
      setSorting(updatedSorting);
    }
    if (onSortingChange) {
      const newSorting = typeof updatedSorting === 'function' ? updatedSorting(sorting) : updatedSorting;
      if (newSorting.length > 0) {
        onSortingChange({
          key: newSorting[0].id,
          direction: newSorting[0].desc ? 'desc' : 'asc',
        });
      } else {
        onSortingChange({});
      }
    }
  };

  const handleRowSelectionChange = (updatedSelection: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
    if (!rowSelectionOverride) {
      setRowSelection(updatedSelection);
    }
    if (onRowSelectionChange) {
      const newSelection = typeof updatedSelection === 'function' ? updatedSelection(rowSelectionOverride ?? rowSelection) : updatedSelection;
      onRowSelectionChange(newSelection);
    }
  };

  const handleColumnVisibilityChange = (updatedVisibility: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => {
    if (!columnVisibilityOverride) {
      setColumnVisibility(updatedVisibility);
    }
    if (onColumnVisibilityChange) {
      const newVisibility = typeof updatedVisibility === 'function' ? updatedVisibility(columnVisibilityOverride ?? columnVisibility) : updatedVisibility;
      onColumnVisibilityChange(newVisibility);
    }
  };

  const handleGroupingChange = (updatedGrouping: GroupingState | ((prev: GroupingState) => GroupingState)) => {
    if (!groupingOverride) {
      setGrouping(updatedGrouping);
    }
    if (onGroupingChange) {
      const newGrouping = typeof updatedGrouping === 'function' ? updatedGrouping(groupingOverride ?? grouping) : updatedGrouping;
      onGroupingChange(newGrouping);
    }
  };

  const handleColumnOrderChange = (updatedColumnOrder: ColumnOrderState | ((prev: ColumnOrderState) => ColumnOrderState)) => {
    if (!columnOrderOverride) {
      setColumnOrder(updatedColumnOrder);
    }
    if (onColumnOrderChange) {
      const newColumnOrder = typeof updatedColumnOrder === 'function' ? updatedColumnOrder(columnOrderOverride ?? columnOrder) : updatedColumnOrder;
      onColumnOrderChange(newColumnOrder);
    }
  };

  const handleColumnFiltersChange = (updatedFilters: ColumnFiltersState | ((prev: ColumnFiltersState) => ColumnFiltersState)) => {
    if (!columnFiltersOverride) {
      setColumnFilters(updatedFilters);
    }
    if (onColumnFiltersChange) {
      const newFilters = typeof updatedFilters === 'function' ? updatedFilters(columnFiltersOverride ?? columnFilters) : updatedFilters;
      onColumnFiltersChange(newFilters);
    }
  };

  function handleColumnFilterPopoverApply(columnId: string, value?: string | string[] | undefined) {
    // If value is empty (undefined, empty string, or empty array), remove the filter entirely
    const isEmpty = value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

    const newFilters = isEmpty
      ? [...(columnFiltersOverride ?? columnFilters).filter((f) => f.id !== columnId)]
      : [...(columnFiltersOverride ?? columnFilters).filter((f) => f.id !== columnId), { id: columnId, value }];

    if (!columnFiltersOverride) {
      setColumnFilters(newFilters);
    }
    onColumnFiltersChange?.(newFilters);
  }

  function handleColumnFilterPopoverRemove(columnId: string) {
    const newFilters = [...(columnFiltersOverride ?? columnFilters).filter((f) => f.id !== columnId)];

    if (!columnFiltersOverride) {
      setColumnFilters(newFilters);
    }
    onColumnFiltersChange?.(newFilters);
  }

  const table = useReactTable<T>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnOrderChange: handleColumnOrderChange,
    onRowSelectionChange: handleRowSelectionChange,
    onSortingChange: handleSortingChange,
    onGroupingChange: handleGroupingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    manualGrouping: false,
    state: {
      sorting: sortingOverride ?? sorting,
      rowSelection: rowSelectionOverride ?? rowSelection,
      grouping: groupingOverride ?? grouping,
      columnVisibility: columnVisibilityOverride ?? columnVisibility,
      columnOrder: columnOrderOverride ?? columnOrder,
      columnFilters: columnFiltersOverride ?? columnFilters,
      expanded: true,
      globalFilter: globalFilter,
    },
    enableExpanding: true,
    enableRowSelection: true,
    groupedColumnMode: false,
  });

  const skeletonLoaderRows = Array.from({ length: 5 }).map((_, index) => (
    <TableRow key={`skeleton-${index}`}>
      {table.getVisibleFlatColumns().map((column) => (
        <TableCell key={`skeleton-cell-${column.id}`}>
          <div className="bg-grey-2 h-4 w-full animate-pulse rounded" />
        </TableCell>
      ))}
    </TableRow>
  ));

  const tableRows = table.getRowModel().rows.map((row) => {
    if (row.getIsGrouped()) {
      const subRows = row.getLeafRows();
      const isAllSelected = row.getIsAllSubRowsSelected();
      const hasOnlyOneSubRow = subRows.length === 1;

      // Align the group header with the data rows' leading select column: render a
      // matching "select all in group" checkbox cell whenever the table has one.
      const visibleCells = row.getVisibleCells();
      const hasLeadingSelectColumn = visibleCells[0]?.column.id === 'select';
      const labelColSpan = hasLeadingSelectColumn ? visibleCells.length - 1 : visibleCells.length;

      return (
        <TableRow key={row.id}>
          {hasLeadingSelectColumn && (
            <TableCell colSpan={1} className={cn('bg-grey-1 w-[40px] max-w-[40px] min-w-[40px]', cellClassName)}>
              <div className="flex w-full justify-center">
                <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={(value) => {
                      // For single-entry groups, explicitly set all sub-rows' selected state
                      if (hasOnlyOneSubRow) {
                        subRows.forEach((subRow) => subRow.toggleSelected(!!value));
                      } else {
                        row.toggleSelected(!!value);
                      }
                    }}
                    aria-label="Select all in group"
                  />
                </label>
              </div>
            </TableCell>
          )}
          <TableCell colSpan={labelColSpan} className={cn('bg-grey-1 font-medium', cellClassName)}>
            {`${row.groupingColumnId ? (row.getGroupingValue(row.groupingColumnId) ?? 'None') : 'None'}`}
          </TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow
        key={row.id}
        data-state={row.getIsSelected() && 'selected'}
        onClick={(event) => {
          // Don't navigate if clicking on interactive elements
          if (event.target instanceof Element && event.target.closest('input, button, a, [role="button"]')) {
            return;
          }
          onRowClick?.(row.original, event);
        }}
        className={onRowClick ? 'cursor-pointer' : ''}>
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} className={cn(cellClassName, cell.column.columnDef.meta?.cellClassName)}>
            {cell.getIsAggregated()
              ? flexRender(cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell, cell.getContext())
              : flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    );
  });

  return (
    <div className={cn('static flex h-full w-full flex-col', containerClassName)}>
      <div>
        <Table className={cn('relative h-full [&_tr_td:first-child]:border-l-0 [&_tr_th:first-child]:border-l-0', tableClassName)}>
          <TableHeader
            className={cn(
              'bg-grey-1 before:bg-grey-3 after:bg-grey-3 sticky top-0 z-10 before:absolute before:top-0 before:right-0 before:left-0 before:h-[1px] after:absolute after:right-0 after:bottom-0 after:left-0 after:h-[1px]',
              headerClassName,
            )}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const hasSupportedFilter = header.column.getCanFilter() && !!header.column.columnDef.meta?.filterType;
                  const columnMeta = header.column.columnDef.meta;

                  const sortTooltip = {
                    asc: columnMeta?.sortTooltip?.asc || 'A-Z',
                    desc: columnMeta?.sortTooltip?.desc || 'Z-A',
                  };

                  return (
                    <TableHead key={header.id} className={cn('border-t-0 border-b-0', headClassName, columnMeta?.cellClassName)}>
                      {header.isPlaceholder ? null : (
                        <div className="flex items-center gap-2 text-nowrap">
                          {header.column.getCanSort() ? (
                            <div className="flex w-full items-center gap-1">
                              <Button
                                type="button"
                                className={cn('flex flex-1 items-center justify-between gap-1 p-0 hover:bg-transparent', sortButtonClassName)}
                                variant="ghost"
                                onClick={() => header.column.toggleSorting(header.column.getIsSorted() === 'asc')}>
                                <div className="font-medium">{flexRender(header.column.columnDef.header, header.getContext())}</div>
                                <TooltipProvider>
                                  <Tooltip delayDuration={0}>
                                    <TooltipTrigger asChild>
                                      <div>
                                        {header.column.getIsSorted() === 'desc' ? (
                                          <ArrowLongDownIcon />
                                        ) : header.column.getIsSorted() === 'asc' ? (
                                          <ArrowLongUpIcon />
                                        ) : (
                                          <ArrowLongDownIcon className="opacity-50" />
                                        )}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipPortal>
                                      <TooltipContent side="bottom" sideOffset={0} className="bg-default-text max-w-[200px] border-none p-1 text-xs text-white">
                                        <TooltipArrow className="fill-default-text" />
                                        {header.column.getIsSorted() === 'asc' ? sortTooltip.asc : sortTooltip.desc}
                                      </TooltipContent>
                                    </TooltipPortal>
                                  </Tooltip>
                                </TooltipProvider>
                              </Button>

                              {header.column.getCanFilter() && hasSupportedFilter && columnMeta?.filterType && (
                                // @ts-expect-error type errors due to discriminated union
                                <ColumnFilterPopover
                                  columnId={header.column.id}
                                  getFilterOptions={columnMeta?.getFilterOptionsFn}
                                  type={columnMeta?.filterType}
                                  title={columnMeta?.filterLabel}
                                  disableNullFilter={columnMeta?.disableNullFilter}
                                  value={
                                    (columnFiltersOverride ?? columnFilters).find((item) => item.id === header.column.id)?.value as
                                      | string
                                      | string[]
                                      | undefined
                                  }
                                  onApply={handleColumnFilterPopoverApply}
                                  onRemove={handleColumnFilterPopoverRemove}
                                />
                              )}
                            </div>
                          ) : (
                            <div className="flex w-full items-center gap-1">
                              <div className="flex-1">{flexRender(header.column.columnDef.header, header.getContext())}</div>
                              {header.column.getCanFilter() && hasSupportedFilter && columnMeta?.filterType && (
                                // @ts-expect-error type errors due to discriminated union
                                <ColumnFilterPopover
                                  columnId={header.column.id}
                                  getFilterOptions={columnMeta.getFilterOptionsFn}
                                  type={columnMeta.filterType}
                                  title={columnMeta.filterLabel}
                                  disableNullFilter={columnMeta.disableNullFilter}
                                  value={
                                    (columnFiltersOverride ?? columnFilters).find((item) => item.id === header.column.id)?.value as
                                      | string
                                      | string[]
                                      | undefined
                                  }
                                  onApply={handleColumnFilterPopoverApply}
                                  onRemove={handleColumnFilterPopoverRemove}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>{loading ? skeletonLoaderRows : tableRows}</TableBody>
        </Table>

        {!loading && table.getRowModel().rows?.length === 0 && (
          <div className={cn('pointer-events-none', notFoundDisplayContainerClassName)}>
            {notFoundDisplay || <div className="py-4 text-center">No results.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
